<template>
  <FormContainer :submit="onSubmit">
    <template v-if="cantFind">
      <BaseAlert hg-type="error" :hg-value="cantFind">
          We cannot locate the address you entered.  Please complete the information below.
      </BaseAlert>
    </template>

    <v-layout row wrap class="locationform--lead">
      <v-flex xs12>
        <StepTitle>Inspection Location</StepTitle>
      </v-flex>

      <v-flex xs12>
        Please enter the address of the inspection below.
        Be sure to include any extra instructions to help us locate the address,
        such as "turn left at the blue barn".
      </v-flex>
    </v-layout>

    <v-layout row wrap class="locationform--address">
      <v-flex xs12>
        <LocationFormAddress v-model="form" />
      </v-flex>
    </v-layout>

    <v-layout row wrap class="locationform--details">
      <v-flex xs12>
        <v-layout row wrap>
          <v-flex xs12>
            <FormSectionTitle>
              Details
            </FormSectionTitle>
            <p>
              Help us prepare for a more efficient inspection by adding
              more information
            </p>

            <button
              v-if="!showDetailsForm"
              class="add-details-button"
              type="button"
              @click.prevent="showDetailsForm = true">
              + Add Detail</button>
          </v-flex>
        </v-layout>
      </v-flex>

      <v-flex xs12 v-if="propertyDetailsIsEnabled && showDetailsForm">
        <LocationFormPropertyDetails v-model="form" :customFields="getCustomFields"/>
      </v-flex>
    </v-layout>

    <v-layout
      v-if="showDetailsForm"
      row
      wrap
      class="locationform--additional">
      <v-flex xs12>
        <LocationFormAdditionalInstructions v-model="form" />
      </v-flex>
    </v-layout>

    <StepNavigation
      :continueDisabled="submitIsDisabled"
      :submitMode="true"
      @submit="onSubmit"
      @save="save" />
  </FormContainer>
</template>

<script>
import { Components, Mixins } from '@spectorasoftware/homegauge-vue';
import { mapGetters } from 'vuex';

import FillForm from '@/mixins/FillForm';
import FormContainer from '@/components/FormContainer.vue';
import FormSectionTitle from '@/components/FormSectionTitle.vue';
import LocationFormAdditionalInstructions from '@/components/LocationFormAdditionalInstructions.vue';
import LocationFormAddress from '@/components/LocationFormAddress.vue';
import LocationFormPropertyDetails from '@/components/LocationFormPropertyDetails.vue';
import StepNavigation from '@/components/StepNavigation.vue';
import StepTitle from '@/components/StepTitle.vue';

const { BaseAlert } = Components;
const { FormValidationsMixin } = Mixins;

export default {
  components: {
    BaseAlert,
    FormContainer,
    FormSectionTitle,
    LocationFormAdditionalInstructions,
    LocationFormAddress,
    LocationFormPropertyDetails,
    StepNavigation,
    StepTitle,
  },
  inject: ['$validator'],
  mixins: [FillForm, FormValidationsMixin],
  props: {
    model: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showDetailsForm: false,
      cantFind: false,
      form: {
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        additionalInstructions: '',
        isOccupied: false,
        formattedDates: {},
      },
    };
  },
  computed: {
    ...mapGetters('Appointment', ['hasLocationPropertyDetails', 'getCustomFields']),
    ...mapGetters('Company', ['propertyDetailsIsEnabled']),
    submitIsDisabled() {
      return !this.formRequiredFieldsAreValid || this.formHasErrors;
    },
    customFieldsContainRequired() {
      return this.getCustomFields.some(field => field.required);
    },
  },
  mounted() {
    this.form.address1 = this.model.address1;
    this.form.address2 = this.model.address2;
    this.form.city = this.model.city;
    this.form.state = this.model.state;
    this.form.postalCode = this.model.postalCode;
    this.form.country = this.model.country;
    this.form.latitude = this.model.latitude;
    this.form.longitude = this.model.longitude;
    this.form.additionalInstructions = this.model.additionalInstructions;
    this.form.isOccupied = this.model.isOccupied;
    this.showDetailsForm = !!(this.hasLocationPropertyDetails) || this.customFieldsContainRequired;

    const cust = this.getCustomFields.reduce((acc, field) => {
      acc[field.id] = this.model[field.id] || null;
      return acc;
    }, {});
    this.form = { ...this.form, ...cust };
  },
  methods: {
    onSubmit() {
      this.$validator.validateAll()
        .then(res => {
          if (res) {
            this.$emit('submit', this.form);
          }
        });
    },
    save() {
      this.$emit('save', this.form);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/vars/theme';

$form-section-padding: 30px;

.add-details-button {
  background-color: $theme-color-white;
  border: 2px solid #eeeff3;
  border-radius: 5px;
  color: $theme-color-secondary;
  cursor: pointer;
  font-weight: bold;
  margin-top: 20px;
  padding: 10px;
  text-transform: uppercase;
  width: 130px;
}

.locationform {
  &--address,
  &--details {
    padding-bottom: $form-section-padding;
  }

  &--additional {
    padding-bottom: 0;
  }
}
</style>
